
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogFooter } from "@/components/ui/dialog";
import { Task } from "../../../types/tasks";
import { AlertCircle } from "lucide-react";

interface TaskEditFormProps {
  task: Task;
  onUpdateTask: (task: Task) => void;
  onClose: () => void;
  pendingRequestsCount: number;
}

const TaskEditForm = ({
  task,
  onUpdateTask,
  onClose,
  pendingRequestsCount,
}: TaskEditFormProps) => {
  const [editedTask, setEditedTask] = useState<Task>({ ...task });
  
  // Placeholder for assignees - in a real app, this would come from your API
  const assignees = [
    { id: "1", name: "Sarah Johnson", avatar: "https://i.pravatar.cc/150?u=sarah" },
    { id: "2", name: "Michael Chen", avatar: "https://i.pravatar.cc/150?u=michael" },
    { id: "3", name: "Emily Rodriguez", avatar: "https://i.pravatar.cc/150?u=emily" }
  ];
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditedTask({ ...editedTask, [e.target.name]: e.target.value });
  };
  
  const handleSelectChange = (field: keyof Task, value: string) => {
    setEditedTask({ ...editedTask, [field]: value });
  };
  
  const handleAssigneeChange = (assigneeId: string) => {
    if (assigneeId === "unassigned") {
      setEditedTask({ ...editedTask, assignee: undefined });
    } else {
      const selectedAssignee = assignees.find(a => a.id === assigneeId);
      if (selectedAssignee) {
        setEditedTask({ 
          ...editedTask, 
          assignee: {
            id: selectedAssignee.id,
            name: selectedAssignee.name,
            avatar: selectedAssignee.avatar
          }
        });
      }
    }
  };
  
  const handleSave = () => {
    onUpdateTask(editedTask);
  };

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="title">Title</Label>
        <Input 
          id="title" 
          name="title" 
          value={editedTask.title} 
          onChange={handleChange} 
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea 
          id="description" 
          name="description" 
          value={editedTask.description} 
          onChange={handleChange} 
          rows={3} 
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select 
            value={editedTask.status} 
            onValueChange={(value) => handleSelectChange("status", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="priority">Priority</Label>
          <Select 
            value={editedTask.priority} 
            onValueChange={(value) => handleSelectChange("priority", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="dueDate">Due Date</Label>
          <Input 
            id="dueDate" 
            name="dueDate" 
            value={editedTask.dueDate} 
            onChange={handleChange} 
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="estimatedTime">Estimated Time</Label>
          <Input 
            id="estimatedTime" 
            name="estimatedTime" 
            value={editedTask.estimatedTime} 
            onChange={handleChange} 
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="assignee">Assignee</Label>
        <Select 
          value={editedTask.assignee?.id || "unassigned"} 
          onValueChange={handleAssigneeChange}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select assignee" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {assignees.map(assignee => (
              <SelectItem key={assignee.id} value={assignee.id}>
                <div className="flex items-center">
                  <div className="h-6 w-6 mr-2 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-xs font-semibold text-primary">
                      {assignee.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  {assignee.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Show pending change requests notification for admins */}
      {pendingRequestsCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-amber-500" />
          <p className="text-sm text-amber-800">
            This task has {pendingRequestsCount} pending change request{pendingRequestsCount > 1 ? 's' : ''}
          </p>
        </div>
      )}
      
      <DialogFooter>
        <Button variant="outline" onClick={onClose} className="mr-2">
          Cancel
        </Button>
        <Button onClick={handleSave}>Save Changes</Button>
      </DialogFooter>
    </>
  );
};

export default TaskEditForm;
